/* eslint-disable eqeqeq */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Dropdown, Table } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import ReactPaginate from "react-paginate";
import { useTranslate } from "../hooks/LanguageProvider";
import { NavLink } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import {
  Distance,
  DownloadModal,
  generateExcelFile,
  generatePDFFile,
  handleDownloadConfirm,
  toTimestamp,
  useClipboard,
  addHiddenColumn,
  removeHiddenColumn,
  getHiddenColumns,
} from "../utilities/functions";
import { toast } from "react-toastify";
import DeleteVehicleModal from "../components/Vehicle/VehicleModal";
import { usePagePreferences } from "../hooks/usePagePreferences";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface Vehicle {
  id_vehicule: number;
  immatriculation_vehicule: string;
  category_vehicule: string;
  vehicule_type: string;
  couleur_vehicule: string;
  id_dispositif: number;
  PSN: string;
  nom_groupe: string | null;
  preusername_user: string;
  username_user: string;
  color_groupe: string | null;
  IB_CODE: string;
  GPSDIST: number;
  nom_conducteur: string | null;
  prenom_conducteur: string | null;
  date_creation_vehicule: string;
  vehicle_door_number: string;
}

export function Vehicles() {
  const PAGE_ID = 6;
  const id_user = localStorage.getItem("userid");
  const id_user_number = Number(localStorage.getItem("userid") ?? 0);

  const { translate } = useTranslate();
  const {
    preferences: listPreferences,
    setPreference: setListPreference,
    setPreferences: setListPreferences,
    loaded: listPreferencesLoaded,
  } = usePagePreferences("vehicles", {
    limitValue: 10,
    currentPage: 1,
    column: "immatriculation_vehicule",
    sort: "ASC",
    search: "",
    type: 2,
    typeSearch: "Immatriculation",
  });
  const [list_vehicle, setVehicles] = useState<Vehicle[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [limitValue, setSelectedLimitValue] = useState(Number(listPreferences.limitValue) || 10);
  const [loading, setLoading] = useState(true);

  const [modalStatus, setModalStatus] = useState<string | null>(null);
  const [titleStatus, setTitleStatus] = useState<string | null>(null);
  const [IdUser, setIdUser] = useState<number>(0);
  const [IdVehicle, setIdVehicle] = useState<number>(0);

  const [currentPage, setCurrentPage] = useState<number>(Number(listPreferences.currentPage) || 1);
  const [column, setSortColumn] = useState(String(listPreferences.column || "immatriculation_vehicule"));
  const [sort, setSort] = useState(String(listPreferences.sort || "ASC"));

  const [search, setSearch] = useState(String(listPreferences.search || ""));
  const [type, setType] = useState(Number(listPreferences.type) || 2);
  const [typeSearch, setTypeSearch] = useState(String(listPreferences.typeSearch || "Immatriculation"));
  const [selectedSearch, setSelectedSearch] = useState(
    translate("Immatriculation")
  );

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const preferencesHydratedRef = useRef(false);
  const { copyToClipboard, copiedId } = useClipboard(
    translate("Matriculation Copied")
  );

  const [selectedVehicles, setSelectedVehicles] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isVehiclesSelected, setIsVehiclesSelected] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState({
    id_vehicule: true,
    vehicule_type: true,
    immatriculation_vehicule: true,
    PSN: true,
    username_user: true,
    nom_groupe: true,
    GPSDIST: true,
    vehicle_door_number: true,
  });

  // ✅ Load hidden columns from DB
  useEffect(() => {
    (async () => {
      if (!id_user_number) return;
      try {
        const hidden = await getHiddenColumns(id_user_number, PAGE_ID);
        setSelectedColumns((prev: any) => {
          const next = { ...prev };
          hidden.forEach((key: string) => {
            if (key in next) next[key] = false;
          });
          return next;
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [id_user_number]);

  const searchColumn: { [key: string]: number } = {
    id_vehicule: 0,
    vehicule_type: 1,
    immatriculation_vehicule: 2,
    PSN: 3,
    username_user: 4,
    nom_groupe: 5,
    GPSDIST: 6,
    vehicle_door_number: 7,
  };

  const vehicleHeaders = [
    translate("ID"),
    translate("Type"),
    translate("Matriculation"),
    translate("PSN"),
    translate("User"),
    translate("Group"),
    translate("Odometer"),
    translate("Door number"),
  ];

  const downloadVehicleExcel = () => {
    const selectedData = list_vehicle
      .filter((vehicle) =>
        selectedVehicles.includes(vehicle.id_vehicule.toString())
      )
      .map((vehicle) => [
        vehicle.id_vehicule,
        vehicle.vehicule_type,
        vehicle.immatriculation_vehicule,
        vehicle.PSN,
        vehicle.username_user,
        vehicle.nom_groupe,
        vehicle.GPSDIST,
        vehicle.vehicle_door_number,
      ]);

    generateExcelFile(
      translate("List") + " " + translate("Vehicles"),
      vehicleHeaders,
      selectedData
    );
  };

  const downloadVehiclePDF = () => {
    const selectedData = list_vehicle
      .filter((vehicle) =>
        selectedVehicles.includes(vehicle.id_vehicule.toString())
      )
      .map((vehicle) => [
        vehicle.id_vehicule,
        vehicle.vehicule_type,
        vehicle.immatriculation_vehicule,
        vehicle.PSN,
        vehicle.username_user,
        vehicle.nom_groupe,
        vehicle.GPSDIST,
        vehicle.vehicle_door_number,
      ]);

    generatePDFFile(
      translate("List") + " " + translate("Vehicles"),
      vehicleHeaders,
      selectedData
    );
  };

  const onDownloadConfirm = (format: string) => {
    if (selectedVehicles.length > 0) {
      handleDownloadConfirm(format, downloadVehicleExcel, downloadVehiclePDF);
    } else {
      toast.warn("Please select at least one vehicle", {
        position: "bottom-right",
        autoClose: 3000,
      });
    }
  };

  const handleTypeSearch = (event: any) => {
    const selectedValue = event.target.textContent;

    let nextType = type;
    switch (selectedValue) {
      case translate("ID Vehicle"):
        setSelectedSearch(translate("ID Vehicle"));
        nextType = 0;
        setType(nextType);
        break;
      case translate("Type"):
        setSelectedSearch(translate("Type"));
        nextType = 1;
        setType(nextType);
        break;
      case translate("Matriculation"):
        setSelectedSearch(translate("Matriculation"));
        nextType = 2;
        setType(nextType);
        break;
      case translate("PSN"):
        setSelectedSearch(translate("PSN"));
        nextType = 3;
        setType(nextType);
        break;
      case translate("User"):
        setSelectedSearch(translate("User"));
        nextType = 4;
        setType(nextType);
        break;
      case translate("Groupe"):
        setSelectedSearch(translate("Groupe"));
        nextType = 5;
        setType(nextType);
        break;
      case translate("Odometer"):
        setSelectedSearch(translate("Odometer"));
        nextType = 6;
        setType(nextType);
        break;
      case translate("Door number"):
        setSelectedSearch(translate("Door number"));
        nextType = 7;
        setType(nextType);
        break;
      default:
        break;
    }

    setTypeSearch(selectedValue);
    setListPreferences({ type: nextType, typeSearch: selectedValue });
  };

  const Options = [
    "ID Véhicule",
    "Type",
    "Matriculation",
    "PSN",
    "User",
    "Group",
    "Odometer",
    "Door number",
  ];

  const handleAdvancedSearch = async (event: any) => {
    const newValue = event.target.value;
    setSearch(newValue);
    setListPreference("search", newValue);
    await getVehicles(limitValue, currentPage, newValue, type, column, sort);
  };

  // ✅ Toggle column + sync to DB (preferences)
  const handleColumnChange = async (colKey: string) => {
    if (!id_user_number) return;

    const currentlyVisible = !!(selectedColumns as any)[colKey];
    const nextVisible = !currentlyVisible;

    setSelectedColumns((prev: any) => ({ ...prev, [colKey]: nextVisible }));

    try {
      if (!nextVisible) {
        await addHiddenColumn({
          id_user: id_user_number,
          id_page: PAGE_ID,
          column_name: colKey,
        });
      } else {
        await removeHiddenColumn({
          id_user: id_user_number,
          id_page: PAGE_ID,
          column_name: colKey,
        });
      }
    } catch (e) {
      console.error(e);
      setSelectedColumns((prev: any) => ({
        ...prev,
        [colKey]: currentlyVisible,
      }));
    }
  };

  const handleSortingColum = (currentColumn: string) => {
    const nextSort = sort === "DESC" ? "ASC" : "DESC";
    setSort(nextSort);
    setSortColumn(currentColumn);
    setCurrentPage(1);
    setListPreferences({ sort: nextSort, column: currentColumn, currentPage: 1 });
    getVehicles(limitValue, 1, search, type, currentColumn, nextSort);
  };

  const handleDeleteVehicle = async (id_vehicule: string, IdUser: number) => {
    try {
      setModalStatus("Do you want to delete this vehicle");
      setTitleStatus("Delete vehicule");
      setIdUser(IdUser);
      setIdVehicle(parseInt(id_vehicule));
    } catch (error) {
      console.error(error);
    }
  };

  const closeModal = () => {
    setModalStatus(null);
  };

  const handleSelectChange = async (event: any) => {
    const newValue = event.target.value;
    const nextLimit = parseInt(newValue, 10);
    setCurrentPage(1);
    setSelectedLimitValue(nextLimit);
    setListPreferences({ limitValue: nextLimit, currentPage: 1 });

    const commentsFormServer = await getVehicles(
      nextLimit,
      1,
      search,
      type,
      column,
      sort
    );
    setVehicles(commentsFormServer);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (!listPreferencesLoaded || preferencesHydratedRef.current) return;
    preferencesHydratedRef.current = true;

    const nextLimit = Number(listPreferences.limitValue) || 10;
    const nextPage = Number(listPreferences.currentPage) || 1;
    const nextSearch = String(listPreferences.search || "");
    const nextType = Number(listPreferences.type) || 2;
    const nextTypeSearch = String(listPreferences.typeSearch || "Immatriculation");
    const nextColumn = String(listPreferences.column || "immatriculation_vehicule");
    const nextSort = String(listPreferences.sort || "ASC");

    setSelectedLimitValue(nextLimit);
    setCurrentPage(nextPage);
    setSearch(nextSearch);
    setType(nextType);
    setTypeSearch(nextTypeSearch);
    setSortColumn(nextColumn);
    setSort(nextSort);
    getVehicles(nextLimit, nextPage, nextSearch, nextType, nextColumn, nextSort);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listPreferencesLoaded]);

  const updateVehicleList = async () => {
    getVehicles(limitValue, currentPage, search, type, column, sort);
  };

  const handlePageClick = async (data: any) => {
    const nextPage = data.selected + 1;
    await getVehicles(limitValue, nextPage, search, type, column, sort);
    setCurrentPage(nextPage);
    setListPreference("currentPage", nextPage);
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (modalStatus === "success") {
      const timeoutId = setTimeout(() => {
        setModalStatus(null);
      }, 3000);

      return () => clearTimeout(timeoutId);
    }
  }, [modalStatus]);

  const getVehicles = async (
    limitValue: number,
    currentPage: number,
    search: string,
    type: number,
    column: string,
    sort: string
  ) => {
    const bodyData = JSON.stringify({
      limitValue,
      currentPage,
      search,
      type,
      id_user,
      column: searchColumn[column],
      sort,
    });

    try {
      setLoading(true);

      const total_pages = await fetch(
        `${backendUrl}/api/vehicle/search/totalpage`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: bodyData,
          mode: "cors",
        }
      );

      const totalPages = await total_pages.json();
      const t = totalPages[0]["total"];
      setTotal(t);

      const vehicleResponse = await fetch(`${backendUrl}/api/vehicle/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: bodyData,
        mode: "cors",
      });

      if (!vehicleResponse.ok) {
        console.error("Error retrieving vehicles list");
        setLoading(false);
        return;
      }

      const data = await vehicleResponse.json();
      setPageCount(Math.ceil(t / limitValue));
      setVehicles(data);
      return data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVehiclesSelect = (VehicleID: string) => {
    let updatedSelectedVehicles: string[] = [];

    if (selectAll) {
      updatedSelectedVehicles = selectedVehicles.includes(VehicleID)
        ? selectedVehicles.filter((id) => id !== VehicleID)
        : list_vehicle.map((v) => v.id_vehicule.toString());
    } else {
      updatedSelectedVehicles = selectedVehicles.includes(VehicleID)
        ? selectedVehicles.filter((id) => id !== VehicleID)
        : [...selectedVehicles, VehicleID];
    }

    setSelectedVehicles(updatedSelectedVehicles);
    setIsVehiclesSelected(updatedSelectedVehicles.length > 0);
  };

  useEffect(() => {
    if (selectedVehicles.length === 0 && selectAll) {
      setSelectAll(false);
    }
  }, [selectedVehicles, selectAll]);

  const handleSelectAllVehicles = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      const allVehicleIDs = list_vehicle.map((v) => v.id_vehicule.toString());
      setSelectedVehicles(allVehicleIDs);
      setIsVehiclesSelected(true);
    } else {
      setSelectedVehicles([]);
      setIsVehiclesSelected(false);
    }
  };

  return (
    <>
      <div className="row">
        <h4 className="col-sm-6 col-md-6">
          <i className="las la-car" data-rel="bootstrap-tooltip" title="Increased"></i>
          {translate("Vehicles")} ({total})
        </h4>

        <div className="col-sm-6 col-md-6 ">
          <div
            id="DataTables_Table_0_filter"
            className="float-right dataTables_filter mr-3"
          >
            <NavLink to="/Vehicle/add" className="btn btn-primary  mr-1">
              <i className="las la-plus mr-3"></i>
              {translate("New vehicle")}
            </NavLink>

            <button
              className="btn btn-outline-secondary mr-1"
              onClick={() => setShowDownloadModal(true)}
            >
              <i className="las la-download"></i>
              {translate("Export")}
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div
          className="col-md-4"
          style={{ margin: "0px 0px 10px 0px", padding: "10px" }}
        >
          <div className="input-group">
            <Dropdown>
              <Dropdown.Toggle
                variant="link"
                id="dropdown-custom"
                className="custom-toggle"
              >
                <i className="fas fa-chevron-down" style={{ fontSize: "20" }}></i>
              </Dropdown.Toggle>
              <Dropdown.Menu onClick={handleTypeSearch}>
                {Options.map((option) => (
                  <Dropdown.Item
                    key={option}
                    className={`${selectedSearch == translate(option) ? "active" : ""}`}
                    onClick={() => setSelectedSearch(translate(option))}
                  >
                    {translate(option)}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <input
              type="text"
              placeholder={` By ${typeSearch}`}
              onChange={handleAdvancedSearch}
              className="form-control"
            />
          </div>
        </div>

        <div className="col-md-8 d-flex justify-content-end align-items-center">
          <div className="dataTables_length" id="DataTables_Table_0_length">
            <label style={{ marginBottom: "0" }}>
              {translate("Show")}
              <select
                name="DataTables_Table_0_length"
                aria-controls="DataTables_Table_0"
                className="custom-select custom-select-sm form-control form-control-sm ml-2"
                style={{ width: "66px" }}
                value={limitValue}
                onChange={handleSelectChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
              </select>
            </label>
          </div>

          <Dropdown>
            <Dropdown.Toggle variant="" id="dropdown-basic" title={translate("Display columns")}>
              <i className="las la-eye"></i>
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.id_vehicule}
                  onChange={() => handleColumnChange("id_vehicule")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("ID")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.vehicule_type}
                  onChange={() => handleColumnChange("vehicule_type")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Type")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.immatriculation_vehicule}
                  onChange={() => handleColumnChange("immatriculation_vehicule")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Matriculation")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.PSN}
                  onChange={() => handleColumnChange("PSN")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("PSN")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.username_user}
                  onChange={() => handleColumnChange("username_user")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("User")}</span>
              </Dropdown.Item>

              {/* ✅ FIXED: Group checkbox must toggle nom_groupe */}
              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.nom_groupe}
                  onChange={() => handleColumnChange("nom_groupe")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Group")}</span>
              </Dropdown.Item>

              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.GPSDIST}
                  onChange={() => handleColumnChange("GPSDIST")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Odometer")}</span>
              </Dropdown.Item>

              {/* ✅ FIXED: Door number checkbox must bind to vehicle_door_number */}
              <Dropdown.Item as="button" style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={selectedColumns.vehicle_door_number}
                  onChange={() => handleColumnChange("vehicle_door_number")}
                />
                <span style={{ marginLeft: "10px" }}>{translate("Door number")}</span>
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <div className="row">
        <div className="table-responsive">
          <Table className="dataTable">
            <thead className="bg-white text-uppercase">
              <tr className="ligth ligth-data">
                <th>
                  <div className="checkbox d-inline-block">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAllVehicles(e.target.checked)}
                    />
                    <label className="form-check-label"></label>
                  </div>
                </th>

                {selectedColumns.id_vehicule && (
                  <th className="sorting" onClick={() => handleSortingColum("id_vehicule")}>
                    {translate("ID vehicle")}
                  </th>
                )}

                {selectedColumns.vehicule_type && (
                  <th className="sorting" onClick={() => handleSortingColum("vehicule_type")}>
                    {translate("Type")}
                  </th>
                )}

                {selectedColumns.immatriculation_vehicule && (
                  <th className="sorting" onClick={() => handleSortingColum("immatriculation_vehicule")}>
                    {translate("Matriculation")}
                  </th>
                )}

                {selectedColumns.PSN && (
                  <th className="sorting" onClick={() => handleSortingColum("PSN")}>
                    {translate("PSN")}
                  </th>
                )}

                {selectedColumns.username_user && (
                  <th className="sorting" onClick={() => handleSortingColum("username_user")}>
                    {translate("User")}
                  </th>
                )}

                {selectedColumns.nom_groupe && (
                  <th className="sorting" onClick={() => handleSortingColum("nom_groupe")}>
                    {translate("Group")}
                  </th>
                )}

                {selectedColumns.GPSDIST && (
                  <th className="sorting" onClick={() => handleSortingColum("GPSDIST")}>
                    {translate("Odometer")}
                  </th>
                )}

                {selectedColumns.vehicle_door_number && (
                  <th className="sorting" onClick={() => handleSortingColum("vehicle_door_number")}>
                    {translate("Door number")}
                  </th>
                )}

                <th>{translate("Actions")}</th>
              </tr>
            </thead>

            <tbody className="ligth-body">
              {loading ? (
                <tr style={{ textAlign: "center" }}>
                  <td className="text-center" colSpan={20}>
                    <p>
                      <PropagateLoader color={"#123abc"} loading={loading} size={20} />
                    </p>
                  </td>
                </tr>
              ) : list_vehicle.length > 0 ? (
                list_vehicle.map((item) => (
                  <tr key={item.id_vehicule}>
                    <td>
                      <div className="checkbox d-inline-block">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`checkbox-${item.id_vehicule}`}
                          checked={selectedVehicles.includes(item.id_vehicule.toString())}
                          onChange={() => handleVehiclesSelect(item.id_vehicule.toString())}
                        />
                        <label htmlFor={`checkbox-${item.id_vehicule}`} className="mb-0"></label>
                      </div>
                    </td>

                    {selectedColumns.id_vehicule && <td>{item.id_vehicule}</td>}

                    {selectedColumns.vehicule_type && (
                      <td>
                        <div className="align-items-center">
                          <span
                            className="icon icon-none"
                            style={{ background: item.couleur_vehicule }}
                          ></span>
                          <div>
                            {item.category_vehicule}
                            <span className="mb-0">
                              <small> ( {item.vehicule_type} )</small>
                            </span>
                          </div>
                        </div>
                      </td>
                    )}

                    {selectedColumns.immatriculation_vehicule && (
                      <td
                        id={`vehicle-${item.id_vehicule}`}
                        style={{
                          cursor: "pointer",
                          position: "relative",
                          color:
                            copiedId === item.id_vehicule.toString()
                              ? "#28a745"
                              : "#007bff",
                        }}
                        title={translate("Click to copy the registration matriculation")}
                        onClick={() => {
                          if (item.immatriculation_vehicule) {
                            copyToClipboard(item.immatriculation_vehicule, item.id_vehicule.toString());
                          }
                        }}
                      >
                        <>
                          <span
                            style={{
                              color:
                                copiedId === item.id_vehicule.toString()
                                  ? "#28a745"
                                  : "#007bff",
                            }}
                          >
                            {item.immatriculation_vehicule}
                          </span>

                          {copiedId === item.id_vehicule.toString() && (
                            <span
                              style={{
                                position: "absolute",
                                top: "-20px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "#28a745",
                                color: "#fff",
                                padding: "2px 5px",
                                borderRadius: "4px",
                                fontSize: "12px",
                              }}
                            >
                              {translate("Matriculation Copied")}
                            </span>
                          )}
                        </>
                      </td>
                    )}

                    {selectedColumns.PSN && <td>{item.PSN}</td>}

                    {selectedColumns.username_user && (
                      <td>{item.username_user !== "none" ? item.username_user : translate("None")}</td>
                    )}

                    {selectedColumns.nom_groupe && (
                      <td>{item.nom_groupe !== "none" ? item.nom_groupe : translate("None")}</td>
                    )}

                    {selectedColumns.GPSDIST && <td>{Distance(item.GPSDIST)}</td>}

                    {selectedColumns.vehicle_door_number && (
                      <td>{item.vehicle_door_number}</td>
                    )}

                    <td>
                      <div className="d-flex align-items-center list-action">
                        <NavLink
                          to={`/Vehicle/edit/${item.id_vehicule}`}
                          className="badge bg-success mr-2"
                          data-toggle="tooltip"
                          data-placement="top"
                          title={translate("Edit")}
                        >
                          <i className="ri-pencil-line mr-0"></i>
                        </NavLink>

                        <a
                          className="badge bg-warning mr-2"
                          onClick={() =>
                            handleDeleteVehicle(
                              item.id_vehicule.toString(),
                              parseInt(id_user ?? "0", 10)
                            )
                          }
                          data-toggle="tooltip"
                          data-placement="top"
                          title={translate("Delete")}
                        >
                          <i className="ri-delete-bin-line mr-0"></i>
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={20}>No vehicles available</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 d-flex align-items-center">
          <span>
            {translate("Displaying")} {list_vehicle.length} {translate("out of")} {total}
          </span>
        </div>

        <div className="col-md-6">
          <ReactPaginate
            previousLabel={translate("previous")}
            nextLabel={translate("next")}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            containerClassName={"pagination justify-content-end"}
            pageClassName={"page-item"}
            pageLinkClassName={"page-link"}
            previousClassName={"page-item"}
            previousLinkClassName={"page-link"}
            nextClassName={"page-item"}
            nextLinkClassName={"page-link"}
            breakClassName={"page-item"}
            breakLinkClassName={"page-link"}
            activeClassName={"active"}
            forcePage={currentPage - 1}
          />
        </div>

        <DeleteVehicleModal
          show={modalStatus !== null}
          onHide={closeModal}
          status={modalStatus}
          title={titleStatus}
          IdUser={IdUser}
          IdVehicle={IdVehicle}
          updateVehicleList={updateVehicleList}
        />

        <DownloadModal
          show={showDownloadModal}
          onHide={() => setShowDownloadModal(false)}
          onDownloadConfirm={onDownloadConfirm}
        />
      </div>
    </>
  );
}
